import React, { useEffect, useState, useRef } from "react";
import managedAxios, { isCancelError } from "@/utils/managedAxios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { API_BASE } from "@/App";
import PaywallNotice from "@/components/PaywallNotice";
import { useAuth } from "@/auth/AuthContext";
import { Badge } from "@/components/ui/badge";
import {
  FileText, Sparkles, Upload, Map, CreditCard, BookOpen,
  Lock, Brain, Zap, Target, CheckCircle2, Trash2
} from "lucide-react";

const NotesPage = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [notes, setNotes] = useState([]);
  const [summary, setSummary] = useState(null);
  const [mindmap, setMindmap] = useState(null);
  const [flashcards, setFlashcards] = useState([]);
  const [activeTab, setActiveTab] = useState("notes");
  const [loading, setLoading] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const fileInputRef = useRef(null);

  const isPro = user?.role === "PRO";

  const loadNotes = async () => {
    try {
      const { data } = await managedAxios.get(`${API_BASE}/notes/text`);
      setNotes(data || []);
    } catch (e) {
      // ignore soft errors
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await managedAxios.delete(`${API_BASE}/notes/text/${noteId}`);
      setNotes((prev) => prev.filter(note => note.id !== noteId));
      toast({ description: "✓ Note deleted successfully" });
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast({ variant: "destructive", description: msg || "Could not delete note." });
    }
  };

  useEffect(() => {
    loadNotes();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await managedAxios.post(`${API_BASE}/notes/text`, { title, content });
      setTitle("");
      setContent("");
      setNotes((prev) => [data, ...prev]);
      toast({ description: "✓ Note saved successfully" });
    } catch (err) {
      toast({ variant: "destructive", description: "Could not save note." });
    } finally {
      setLoading(false);
    }
  };

  const handleSummarize = async () => {
    if (!isPro) {
      toast({ 
        variant: "destructive", 
        description: "🔒 Smart summarization is PRO-only. Upgrade to unlock." 
      });
      navigate("/upgrade");
      return;
    }
    
    setLoading(true);
    try {
      const { data } = await managedAxios.post(`${API_BASE}/notes/summarize`);
      setSummary(data.summary);
      setActiveTab("summary");
      toast({ description: "✓ Summary generated!" });
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast({ variant: "destructive", description: msg || "Could not summarize notes." });
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateMindmap = async () => {
    if (!isPro) {
      toast({ 
        variant: "destructive", 
        description: "🔒 Smart mindmaps are PRO-only. Upgrade to unlock." 
      });
      navigate("/upgrade");
      return;
    }

    setLoading(true);
    try {
      const { data } = await managedAxios.post(`${API_BASE}/notes/mindmap`);
      setMindmap(data);
      setActiveTab("mindmap");
      toast({ description: "✓ Mindmap generated!" });
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast({ variant: "destructive", description: msg || "Could not generate mindmap." });
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateFlashcards = async () => {
    if (!isPro) {
      toast({ 
        variant: "destructive", 
        description: "🔒 Smart flashcards are PRO-only. Upgrade to unlock." 
      });
      navigate("/upgrade");
      return;
    }

    setLoading(true);
    try {
      const { data } = await managedAxios.post(`${API_BASE}/notes/flashcards`);
      setFlashcards(data.cards || []);
      setActiveTab("flashcards");
      toast({ description: `✓ Generated ${data.cards?.length || 0} flashcards!` });
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast({ variant: "destructive", description: msg || "Could not generate flashcards." });
    } finally {
      setLoading(false);
    }
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!isPro) {
      toast({ 
        variant: "destructive", 
        description: "🔒 PDF import is PRO-only. Upgrade to unlock." 
      });
      navigate("/upgrade");
      return;
    }

    setUploadingPdf(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const { data } = await managedAxios.post(`${API_BASE}/notes/upload-pdf`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      toast({ description: `✓ ${file.name} uploaded successfully` });
      loadNotes();
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast({ variant: "destructive", description: msg || "Could not upload PDF." });
    } finally {
      setUploadingPdf(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50/30" data-testid="notes-page">
      {/* Premium Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-20 w-72 h-72 bg-teal-100/40 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-emerald-100/40 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 py-10 space-y-8">
      {/* Header - Premium Glassmorphism */}
      <div className="glass-card rounded-2xl p-6 md:p-8 animate-fade-up">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div className="space-y-2">
            <div className="flex items-center gap-3 mb-1">
              <Badge className={`px-3 py-1 ${isPro 
                ? 'bg-gradient-to-r from-teal-500 to-emerald-500 text-white border-0' 
                : 'bg-slate-100 text-slate-600 border-0'}`}>
                {isPro ? <><Zap className="w-3 h-3 mr-1" /> PRO Tools</> : 'Basic Notes'}
              </Badge>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-500 flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-white" />
              </div>
              Notes Workspace
            </h1>
            <p className="text-slate-600">
              Create, organize and transform your notes with smart tools
            </p>
          </div>
        </div>
      </div>

      {/* Features Grid - Premium Cards */}
      <div className="grid gap-4 md:grid-cols-4 animate-fade-up" style={{animationDelay: '100ms'}}>
        <div className="glass-card rounded-xl p-4 space-y-2">
          <div className="flex items-center justify-between">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center">
              <FileText className="w-4 h-4 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-900">{notes.length}</span>
          </div>
          <p className="text-xs font-semibold text-slate-900">Total Notes</p>
          <p className="text-[11px] text-slate-500">All your study material</p>
        </div>

        <div className="glass-card rounded-xl p-4 space-y-2">
          <div className="flex items-center justify-between">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
              <Upload className="w-4 h-4 text-white" />
            </div>
            {!isPro && <Lock className="w-4 h-4 text-slate-400" />}
          </div>
          <p className="text-xs font-semibold text-slate-900">PDF Import</p>
          <p className="text-[11px] text-slate-500">{isPro ? 'Upload & extract' : 'PRO feature'}</p>
        </div>

        <div className="glass-card rounded-xl p-4 space-y-2">
          <div className="flex items-center justify-between">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
              <Map className="w-4 h-4 text-white" />
            </div>
            {!isPro && <Lock className="w-4 h-4 text-slate-400" />}
          </div>
          <p className="text-xs font-semibold text-slate-900">Smart Mindmaps</p>
          <p className="text-[11px] text-slate-500">{isPro ? 'Visual learning' : 'PRO feature'}</p>
        </div>

        <div className="glass-card rounded-xl p-4 space-y-2">
          <div className="flex items-center justify-between">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
              <CreditCard className="w-4 h-4 text-white" />
            </div>
            {!isPro && <Lock className="w-4 h-4 text-slate-400" />}
          </div>
          <p className="text-xs font-semibold text-slate-900">Flashcards</p>
          <p className="text-[11px] text-slate-500">{isPro ? 'Active recall' : 'PRO feature'}</p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2 items-start">
        {/* Create Note Section */}
        <Card className="border-slate-200 bg-white shadow-sm">
          <CardContent className="p-6 space-y-5">
            <div className="flex items-center gap-3">
              <div className="bg-teal-100 p-2 rounded-lg">
                <FileText className="w-5 h-5 text-teal-600" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-slate-900">Create New Note</h2>
                <p className="text-xs text-slate-600">Build your personal knowledge base</p>
              </div>
            </div>

            <form className="space-y-4" onSubmit={handleCreate}>
              <div className="space-y-2">
                <p className="text-[11px] font-semibold tracking-[0.12em] uppercase text-slate-700">
                  TITLE
                </p>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g., Polity - Fundamental Rights"
                  required
                  className="text-sm"
                />
              </div>

              <div className="space-y-2">
                <p className="text-[11px] font-semibold tracking-[0.12em] uppercase text-slate-700">
                  CONTENT
                </p>
                <Textarea
                  rows={8}
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Key points, schemes, committees, important facts..."
                  required
                  className="text-sm resize-none"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 text-white shadow-lg"
                disabled={loading}
              >
                {loading ? (
                  <>Saving...</>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Save Note
                  </>
                )}
              </Button>
            </form>

            {/* PDF Upload */}
            <div className="pt-4 border-t border-slate-200">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handlePdfUpload}
                accept=".pdf"
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingPdf}
              >
                {uploadingPdf ? (
                  <>Uploading...</>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    {isPro ? 'Upload PDF' : 'Upload PDF (PRO)'}
                    {!isPro && <Lock className="w-3 h-3 ml-2" />}
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* AI Tools & Display Section */}
        <div className="space-y-4">
          {/* AI Tools Bar */}
          <Card className="border-slate-200 bg-white shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  size="sm"
                  variant={activeTab === "notes" ? "default" : "outline"}
                  onClick={() => setActiveTab("notes")}
                  className="flex-1 sm:flex-none"
                >
                  <FileText className="w-3 h-3 mr-1" />
                  Notes
                </Button>
                <Button
                  size="sm"
                  variant={activeTab === "summary" ? "default" : "outline"}
                  onClick={handleSummarize}
                  disabled={loading || notes.length === 0}
                  className="flex-1 sm:flex-none"
                >
                  <Brain className="w-3 h-3 mr-1" />
                  Summary
                  {!isPro && <Lock className="w-3 h-3 ml-1" />}
                </Button>
                <Button
                  size="sm"
                  variant={activeTab === "mindmap" ? "default" : "outline"}
                  onClick={handleGenerateMindmap}
                  disabled={loading || notes.length === 0}
                  className="flex-1 sm:flex-none"
                >
                  <Map className="w-3 h-3 mr-1" />
                  Mindmap
                  {!isPro && <Lock className="w-3 h-3 ml-1" />}
                </Button>
                <Button
                  size="sm"
                  variant={activeTab === "flashcards" ? "default" : "outline"}
                  onClick={handleGenerateFlashcards}
                  disabled={loading || notes.length === 0}
                  className="flex-1 sm:flex-none"
                >
                  <CreditCard className="w-3 h-3 mr-1" />
                  Cards
                  {!isPro && <Lock className="w-3 h-3 ml-1" />}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Content Display */}
          {activeTab === "notes" && (
            <Card className="border-slate-200 bg-white shadow-sm">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-bold text-slate-900">Your Notes</p>
                  <Badge variant="outline" className="text-xs">
                    {notes.length} saved
                  </Badge>
                </div>

                {notes.length > 0 ? (
                  <div className="space-y-3 max-h-[500px] overflow-y-auto">
                    {notes.map((note) => (
                      <Card key={note.id} className="border-l-4 border-teal-400 bg-gradient-to-r from-teal-50 to-transparent">
                        <CardContent className="p-4 space-y-2">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1">
                              <p className="text-sm font-bold text-slate-900">{note.title}</p>
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteNote(note.id)}
                              className="h-7 w-7 p-0 hover:bg-red-100 hover:text-red-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                          <p className="text-xs text-slate-700 line-clamp-3">{note.content}</p>
                          <p className="text-[11px] text-slate-500">
                            {new Date(note.created_at).toLocaleDateString()}
                          </p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-sm text-slate-500">No notes yet. Create your first note above!</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {activeTab === "summary" && (
            <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 shadow-sm">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-center gap-2">
                  <Brain className="w-5 h-5 text-emerald-600" />
                  <p className="text-sm font-bold text-emerald-900">AI Summary</p>
                </div>
                
                {summary ? (
                  <div className="bg-white rounded-lg border border-emerald-200 p-4">
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">{summary}</p>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Sparkles className="w-12 h-12 text-emerald-300 mx-auto mb-3" />
                    <p className="text-sm text-slate-600">Click "Summary" to generate smart summary</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {activeTab === "mindmap" && (
            <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-fuchsia-50 shadow-sm">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-center gap-2">
                  <Map className="w-5 h-5 text-purple-600" />
                  <p className="text-sm font-bold text-purple-900">Smart Mindmap</p>
                </div>

                {mindmap ? (
                  <div className="bg-white rounded-lg border border-purple-200 p-4">
                    <MindmapRenderer node={mindmap.root} />
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Map className="w-12 h-12 text-purple-300 mx-auto mb-3" />
                    <p className="text-sm text-slate-600">Click "Mindmap" to generate visual structure</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {activeTab === "flashcards" && (
            <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 shadow-sm">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-amber-600" />
                  <p className="text-sm font-bold text-amber-900">AI Flashcards</p>
                </div>

                {flashcards.length > 0 ? (
                  <div className="space-y-3 max-h-[500px] overflow-y-auto">
                    {flashcards.map((card) => (
                      <FlashcardItem key={card.id} card={card} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <CreditCard className="w-12 h-12 text-amber-300 mx-auto mb-3" />
                    <p className="text-sm text-slate-600">Click "Cards" to generate flashcards</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
      </div>
    </div>
  );
};

// Mindmap Renderer Component
const MindmapRenderer = ({ node, level = 0 }) => {
  const colors = ["text-purple-900", "text-indigo-900", "text-blue-900", "text-slate-900"];
  const bgColors = ["bg-purple-100", "bg-indigo-100", "bg-blue-100", "bg-slate-100"];
  
  return (
    <div className={`${level > 0 ? 'ml-6 mt-2' : ''}`}>
      <div className={`flex items-center gap-2 ${level === 0 ? 'mb-4' : 'mb-2'}`}>
        <div className={`${bgColors[Math.min(level, 3)]} px-3 py-1.5 rounded-lg`}>
          <p className={`text-sm font-semibold ${colors[Math.min(level, 3)]}`}>
            {node.label}
          </p>
        </div>
      </div>
      {node.children?.length > 0 && (
        <div className="border-l-2 border-purple-200 pl-2">
          {node.children.map((child) => (
            <MindmapRenderer key={child.id} node={child} level={level + 1} />
          ))}
        </div>
      )}
    </div>
  );
};

// Flashcard Component
const FlashcardItem = ({ card }) => {
  const [flipped, setFlipped] = useState(false);

  return (
    <Card 
      className="border-amber-300 bg-white cursor-pointer hover:shadow-md transition-shadow"
      onClick={() => setFlipped(!flipped)}
    >
      <CardContent className="p-4 space-y-2 min-h-[120px]">
        <div className="flex items-center justify-between">
          <Badge className="bg-amber-100 text-amber-900 text-xs border-amber-300">
            {card.category}
          </Badge>
          <p className="text-[11px] text-slate-500">{flipped ? 'Click to flip' : 'Click to reveal'}</p>
        </div>
        
        {!flipped ? (
          <div className="flex items-center justify-center py-4">
            <p className="text-sm font-semibold text-slate-900 text-center">{card.front}</p>
          </div>
        ) : (
          <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
            <p className="text-sm text-slate-700">{card.back}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default NotesPage;
